import { IconBuildingWarehouse, IconMapPinFilled } from "@tabler/icons-react";
import ReactDOMServer from "react-dom/server";
import { CLUSTER_ICON_MAX_SIZE, CLUSTER_ICON_MIN_SIZE, MARKER_ICON_SIZE } from "./mapConstants";

const toDataUrl = (svg: string) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

/**
 * Rendering an icon to SVG is not free and the same handful of colours repeat
 * across hundreds of markers, so the results are cached per colour.
 */
const pinIconCache = new Map<string, string>();
const clusterIconCache = new Map<string, string>();
const warehouseIconCache = new Map<string, string>();

export const getMarkerIconSvg = (color: string): string => {
  const cached = pinIconCache.get(color);
  if (cached) return cached;

  const url = toDataUrl(
    ReactDOMServer.renderToStaticMarkup(<IconMapPinFilled color={color} size={MARKER_ICON_SIZE} />)
  );
  pinIconCache.set(color, url);
  return url;
};

/**
 * A filled circle with the warehouse icon inside, white rim: the same mark the
 * shipment timeline uses for the starting Site, so map and list match.
 */
export const getWarehouseIconSvg = (color: string): string => {
  const cached = warehouseIconCache.get(color);
  if (cached) return cached;

  const inner = ReactDOMServer.renderToStaticMarkup(
    <IconBuildingWarehouse color="#ffffff" size={18} stroke={2} />
  ).replace("<svg", '<svg x="7" y="7"');
  const url = toDataUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${MARKER_ICON_SIZE}" height="${MARKER_ICON_SIZE}" viewBox="0 0 32 32">` +
      `<circle cx="16" cy="16" r="14" fill="${color}" stroke="#ffffff" stroke-width="2"/>` +
      inner +
      `</svg>`
  );
  warehouseIconCache.set(color, url);
  return url;
};

/** Concentric halo circles in the client's brand colour, count drawn on top. */
export const getClusterIconSvg = (color: string): string => {
  const cached = clusterIconCache.get(color);
  if (cached) return cached;

  const url = toDataUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">` +
      `<circle cx="120" cy="120" r="118" fill="${color}" opacity="0.18"/>` +
      `<circle cx="120" cy="120" r="98" fill="${color}" opacity="0.32"/>` +
      `<circle cx="120" cy="120" r="76" fill="${color}"/>` +
      `</svg>`
  );
  clusterIconCache.set(color, url);
  return url;
};

/** Logarithmic growth: a 1000-point cluster is only ~1.8x a 2-point one. */
export const getClusterIconSize = (count: number): number => {
  const size = CLUSTER_ICON_MIN_SIZE + Math.log10(Math.max(count, 1)) * 16;
  return Math.round(Math.min(size, CLUSTER_ICON_MAX_SIZE));
};
